import { batch } from 'react-redux';
import { type AppThunk } from '../../store';
import { localStorageGet, removeItem } from '../../utils/storage';
import { clearAllChatDrafts } from '../ChatDrafts/chatDraftsSlice';
import { resetAlarmSlice } from '../Alarm/AlarmSlice';
import { resetCompleteChecklistStore } from '../Checklists/checklistsSlice';
import { resetDocumentSlice } from '../Documents/documentsSlice';
import { resetDocumentGroups } from '../GroupsList/groupsSlice';
import { resetUser } from '../Login/LoginSlice';
import { getResolvedApiUrl } from '../../apis/utils/provider';
import isElectron from 'is-electron';
import { logoutRequest } from '../../api/sessions';
import { clearAllTenantStorage } from '../../utils/tenantSessionStorage';
import {
  pruneTenantSession,
  resetTenantSessions,
  selectActiveTenantId,
} from '../Organizations/OrganizationsSlice';
import { deletePushToken, updatePushToken } from '../../api/pushTokens';
import { resetTenantScopedSlices } from '../Organizations/OrganizationsSlice/resetTenantScoped';
import {
  MAX_SIGN_OUT_FALLBACK_ATTEMPTS,
  trySwitchFirstRemainingTenantSession,
} from '../Organizations/OrganizationsSlice/trySwitchFirstRemainingTenantSession';

const resetStores = (): AppThunk => async dispatch => {
  batch(() => {
    dispatch(resetDocumentSlice());
    dispatch(resetDocumentGroups());
    dispatch(resetAlarmSlice());
  });
};

export const logoutUser = (): AppThunk => async dispatch => {
  if (process.env.REACT_APP_ENV_NAME === 'dev') {
    const resolvedApiUrl = getResolvedApiUrl();
    console.log('resolvedApiUrl: ' + resolvedApiUrl);
  }

  removeItem('coreApiAccessToken');
  removeItem('coreApiRefreshToken');
  removeItem('coreApiSessionId');
  removeItem('csrf');
  removeItem('APP_FILTERS');

  batch(() => {
    dispatch(resetStores());
    dispatch(resetUser());
    dispatch(resetCompleteChecklistStore());
    dispatch(clearAllChatDrafts());
    clearAllTenantStorage();
    dispatch(resetTenantSessions());
  });
};

export async function tryUpdatePushToken(): Promise<void> {
  const pushToken = isElectron() ? localStorageGet('pushyToken') : null;
  if (!pushToken) return;

  try {
    await updatePushToken(pushToken);
  } catch {
    // Swallow error to avoid interfering with login flow
  }
}

export async function tryDeletePushToken(): Promise<void> {
  const pushToken = isElectron() ? localStorageGet('pushyToken') : null;
  if (!pushToken) return;

  try {
    await deletePushToken(pushToken);
  } catch {
    // Swallow error to avoid interfering with logout flow
  }
}

/** Signs out only the active tenant and falls forward to another cached one. */
export const signOutFromActiveTenantSession =
  (): AppThunk<Promise<{ hasSwitchedSession: boolean }>> => async (dispatch, getState) => {
    const state = getState();
    const activeTenantId = selectActiveTenantId(state);
    if (!activeTenantId) {
      try {
        await logoutRequest();
      } catch (error) {
        console.warn('signOutFromActiveTenantSession: logoutRequest failed', error);
      }
      await tryDeletePushToken();
      dispatch(logoutUser());
      return {
        hasSwitchedSession: false,
      };
    }

    try {
      await logoutRequest();
    } catch (error) {
      console.warn('signOutFromActiveTenantSession: logoutRequest failed', error);
    }

    dispatch(pruneTenantSession({ tenantId: activeTenantId, alertUser: false }));
    dispatch(resetTenantScopedSlices());

    const hasSwitchedSession = await dispatch(
      trySwitchFirstRemainingTenantSession({
        maxAttempts: MAX_SIGN_OUT_FALLBACK_ATTEMPTS,
      })
    );

    if (!hasSwitchedSession) {
      await tryDeletePushToken();
      dispatch(logoutUser());
    }

    return {
      hasSwitchedSession,
    };
  };
